import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatErr } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", business_type: "", city: "", consent: true, lang: "en" };
const STATUS = { sent: "Email sent", pending: "Sending…", failed: "Send failed", no_consent: "No email (didn't agree)" };

function Row({ r, i, onChange, onRemove }) {
  const set = (k) => (e) => onChange(i, { ...r, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="grid grid-cols-2 md:grid-cols-[1.2fr_1.5fr_1fr_1fr_1fr_auto_auto_auto] gap-2 items-center" data-testid={`ws-row-${i}`}>
      <Input placeholder="Name" value={r.name} onChange={set("name")} data-testid={`ws-row-${i}-name`} />
      <Input placeholder="Email" type="email" value={r.email} onChange={set("email")} data-testid={`ws-row-${i}-email`} />
      <Input placeholder="Phone" value={r.phone} onChange={set("phone")} data-testid={`ws-row-${i}-phone`} />
      <Input placeholder="Business type" value={r.business_type} onChange={set("business_type")} data-testid={`ws-row-${i}-business`} />
      <Input placeholder="City" value={r.city} onChange={set("city")} data-testid={`ws-row-${i}-city`} />
      <select value={r.lang} onChange={set("lang")} className="h-10 border border-slate-300 rounded-md px-2 text-sm bg-white" data-testid={`ws-row-${i}-lang`}><option value="en">English</option><option value="es">Spanish</option></select>
      <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap"><input type="checkbox" checked={r.consent} onChange={set("consent")} data-testid={`ws-row-${i}-consent`} />OK to email</label>
      <button onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-600" aria-label="Remove row" data-testid={`ws-row-${i}-remove`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function Recent({ list, onResend }) {
  if (!list.length) return null;
  return (
    <div className="mt-6 border-t border-slate-100 pt-4" data-testid="ws-contacts-list">
      <h4 className="text-sm font-semibold text-slate-900 mb-2">Recent workshop contacts</h4>
      <div className="divide-y divide-slate-100 text-sm max-h-72 overflow-y-auto">
        {list.map((c) => (
          <div key={c.contact_id} className="py-2 flex flex-wrap items-center gap-3" data-testid={`ws-contact-${c.contact_id}`}>
            <span className="font-medium text-slate-900">{c.name}</span><span className="text-slate-500">{c.email}</span>
            <span className="text-xs text-slate-400">{[c.library, c.date, c.lang === "es" ? "Spanish" : "English"].filter(Boolean).join(" · ")}</span>
            <span className={`ml-auto text-xs font-medium ${c.status === "sent" ? "text-emerald-700" : c.status === "failed" ? "text-red-600" : "text-slate-500"}`} data-testid={`ws-contact-status-${c.contact_id}`}>{STATUS[c.status] || c.status}</span>
            {c.consent && c.status !== "pending" && <button onClick={() => onResend(c.contact_id)} className="text-xs text-emerald-700 hover:underline" data-testid={`ws-contact-resend-${c.contact_id}`}>Resend</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function WorkshopContacts({ workshops = [] }) {
  const [meta, setMeta] = useState({ workshop_id: "", library: "", date: "" });
  const pick = (id) => { const w = workshops.find((x) => x.workshop_id === id); setMeta(w ? { workshop_id: id, library: w.library, date: w.display_date } : { workshop_id: "", library: "", date: "" }); };
  const [rows, setRows] = useState([{ ...EMPTY }]);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/workshop/contacts").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const change = (i, r) => setRows(rows.map((x, j) => (j === i ? r : x)));
  const remove = (i) => setRows(rows.length > 1 ? rows.filter((_, j) => j !== i) : [{ ...EMPTY }]);
  const valid = rows.filter((r) => r.name.trim() && r.email.trim());
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/workshop/contacts", { ...meta, contacts: valid });
      toast.success(`Saved ${r.data.added} contact${r.data.added === 1 ? "" : "s"}${r.data.skipped ? ` (${r.data.skipped} already saved)` : ""}. Follow-up emails are on their way.`);
      setRows([{ ...EMPTY }]);
      setTimeout(load, 2500);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const resend = (id) => api.post(`/admin/workshop/contacts/${id}/resend`).then(() => { toast.success("Follow-up email resent"); load(); }).catch((e) => toast.error(formatErr(e)));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="workshop-contacts">
      <h3 className="font-semibold text-slate-900">Sign-in sheet contacts</h3>
      <p className="text-xs text-slate-500 mt-1 mb-3">Type in each person from the sign-in sheet. Everyone with "OK to email" checked gets the thank-you email with the slides and sign-up link right after you save, in the language you pick.</p>
      {workshops.length > 0 && <select value={meta.workshop_id} onChange={(e) => pick(e.target.value)} className="h-10 w-full border border-slate-300 rounded-md px-2 text-sm bg-white mb-3" data-testid="ws-workshop-select"><option value="">Pick a saved workshop (optional)</option>{workshops.map((w) => <option key={w.workshop_id} value={w.workshop_id}>{w.library} · {w.display_date}</option>)}</select>}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Input placeholder="Library (shown in the email)" value={meta.library} onChange={(e) => setMeta({ ...meta, library: e.target.value })} data-testid="ws-library" />
        <Input placeholder="Workshop date" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} data-testid="ws-date" />
      </div>
      <div className="space-y-2">{rows.map((r, i) => <Row key={i} r={r} i={i} onChange={change} onRemove={remove} />)}</div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setRows([...rows, { ...EMPTY, lang: rows[rows.length - 1].lang }])} data-testid="ws-add-row"><Plus className="h-4 w-4 mr-1" />Add person</Button>
        <Button onClick={save} disabled={busy || !valid.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ws-save">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Save and send follow-ups</Button>
      </div>
      <Recent list={list} onResend={resend} />
    </div>
  );
}
